import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Crown, Stethoscope, Wrench, Telescope, Globe2, Heart } from "lucide-react";

const FLEET = [
    {
        id: "admiral",
        Icon: Crown,
        title: "Fleet Admirals",
        tagline: "Sector leadership & residuals",
        body: "Lead a sub-fleet under your hull. Recruit Technicians and Surgeons. Earn residuals on every placement made in your sector under the 60/30/10 LAW.",
    },
    {
        id: "surgeon",
        Icon: Stethoscope,
        title: "Sovereign Surgeons",
        tagline: "Specialist precision work",
        body: "Senior specialists invited to high-risk dossiers. Deep-dive vulnerability work, priority bounties, retained engagements with named anchor clients.",
    },
    {
        id: "technician",
        Icon: Wrench,
        title: "On-Demand Technicians",
        tagline: "Hands-on field execution",
        body: "Strike-ready operators. Deploy to active missions, execute audits and remediations, paid per engagement on full LAW terms.",
    },
];

const HORIZONS = [
    {
        n: "H1",
        title: "Sovereign Recruitment Hub",
        body: "The flagship hull. Today. White-labeled recruitment platforms with AI-graded candidate vetting, the Foundry Ledger, and Trinity Boarding.",
        state: "live",
    },
    {
        n: "H2",
        title: "Armada Auto-Provisioning",
        body: "Every Fleet Admiral spawns a hull from a single command — subdomain, brand assets, ledger, and Ghost Crew agents wired in under sixty seconds.",
        state: "build",
    },
    {
        n: "H3",
        title: "Intelligence Exchange",
        body: "A clearance-gated marketplace where Surgeons broker high-risk dossiers between hulls. Risk-graded bounties, escrow under the LAW, audit trail enforced.",
        state: "design",
    },
    {
        n: "H4",
        title: "CyberKids Pipeline",
        body: "The 10% Anchor matures into a credentialed training track. Every fee placed today seeds a future Technician — and the fleet replenishes itself.",
        state: "mission",
    },
];

export default function Vision() {
    const { search } = useLocation();
    return (
        <div data-testid="vision-page" className="mx-auto max-w-[1200px] px-6 py-20 md:px-10">
            <header className="max-w-3xl">
                <div className="caption inline-flex items-center gap-2" style={{ color: "var(--brand-accent)" }}>
                    <Telescope size={14} /> The Sovereign Vision
                </div>
                <h1 className="mt-3 font-serif text-5xl tracking-display text-[#FAFAFA] sm:text-7xl leading-[0.95]">
                    A fleet of <em className="italic" style={{ color: "var(--brand-accent)" }}>independent</em> hulls.
                    <br />Bound by one constitution.
                </h1>
                <p className="mt-8 text-lg font-light leading-relaxed text-[#A3A3A3]">
                    The platform recruitment economy is a stack of middlemen extracting rent from operators who do the actual work.
                    The Sovereign Vision inverts that stack. Every hull is owned by the recruiter running it. Every placement
                    settles under the 60/30/10 LAW. Every dollar of platform revenue is tied to a 10% Anchor that funds the
                    next generation of operators. The fleet replenishes itself.
                </p>
            </header>

            {/* Fleet Doctrine */}
            <section className="mt-24">
                <div className="caption text-[var(--brand-accent)]">Fleet Doctrine</div>
                <h2 className="mt-3 font-serif text-4xl tracking-display text-[#FAFAFA] sm:text-5xl">
                    Three ranks. One Armada.
                </h2>
                <p className="mt-3 max-w-2xl text-[#A3A3A3]">
                    Specialization without silos. Each rank earns under the same LAW, but plays a different role in the fleet's velocity.
                </p>
                <div className="mt-10 grid gap-6 md:grid-cols-3">
                    {FLEET.map(({ id, Icon, title, tagline, body }) => (
                        <article
                            key={id}
                            data-testid={`vision-rank-${id}`}
                            className="rounded-md border border-white/5 bg-[#141414]/60 p-7 backdrop-blur-xl transition-all hover:border-[var(--brand-accent)]/40"
                        >
                            <div className="flex items-baseline justify-between gap-3">
                                <Icon size={22} style={{ color: "var(--brand-accent)" }} />
                                <span className="caption text-[var(--brand-accent)]">{tagline}</span>
                            </div>
                            <h3 className="mt-4 font-serif text-2xl tracking-display text-[#FAFAFA]">{title}</h3>
                            <p className="mt-2 text-sm text-[#A3A3A3] leading-relaxed">{body}</p>
                        </article>
                    ))}
                </div>
            </section>

            {/* Horizons */}
            <section className="mt-24">
                <div className="caption text-[var(--brand-accent)]">Strategic Horizons</div>
                <h2 className="mt-3 font-serif text-4xl tracking-display text-[#FAFAFA] sm:text-5xl">
                    Where the fleet is heading.
                </h2>
                <div className="mt-10 grid gap-6 md:grid-cols-2">
                    {HORIZONS.map(({ n, title, body, state }) => (
                        <article
                            key={n}
                            data-testid={`vision-horizon-${n.toLowerCase()}`}
                            className="rounded-md border border-white/5 bg-[#141414]/40 p-7 backdrop-blur-xl"
                        >
                            <div className="flex items-center justify-between">
                                <span className="font-mono text-xs text-[#737373]">HORIZON {n}</span>
                                <HorizonBadge state={state} />
                            </div>
                            <h3 className="mt-4 font-serif text-2xl tracking-display text-[#FAFAFA]">{title}</h3>
                            <p className="mt-3 text-sm text-[#A3A3A3] leading-relaxed">{body}</p>
                        </article>
                    ))}
                </div>
            </section>

            {/* The Anchor */}
            <section
                data-testid="vision-anchor-callout"
                className="mt-24 overflow-hidden rounded-md border p-10 md:p-14"
                style={{
                    borderColor: "rgba(201,169,110,0.35)",
                    background: "linear-gradient(135deg, rgba(201,169,110,0.08) 0%, rgba(10,10,10,0.6) 60%, rgba(10,10,10,1) 100%)",
                }}
            >
                <div className="flex items-center gap-3">
                    <Heart size={20} style={{ color: "var(--brand-accent)" }} />
                    <span className="caption tracking-widest" style={{ color: "var(--brand-accent)" }}>
                        The 10% Anchor · CyberKids
                    </span>
                </div>
                <h2 className="mt-3 font-serif text-4xl tracking-display text-[#FAFAFA] md:text-5xl">
                    A platform that builds its own successors.
                </h2>
                <p className="mt-5 max-w-3xl text-[#A3A3A3] leading-relaxed md:text-lg">
                    Every transaction on every hull routes <span style={{ color: "var(--brand-accent)" }}>10%</span> directly to the CyberKids Anchor —
                    youth cybersecurity training, scholarships, and curriculum. We don't fund this from "profits." It's
                    written into the LAW. The fleet's first line of revenue is the fleet's next generation.
                </p>
                <div className="mt-8 flex items-center gap-3">
                    <Globe2 size={14} className="text-[#A3A3A3]" />
                    <span className="text-xs text-[#A3A3A3]">
                        Disbursed manually until automated Payouts are enabled. Tracked transparently in the Foundry Ledger.
                    </span>
                </div>
            </section>

            <section className="mt-20 flex flex-wrap items-center justify-between gap-6 rounded-md border border-white/5 bg-[#141414]/60 p-10 backdrop-blur-xl">
                <div>
                    <div className="caption text-[var(--brand-accent)]">Enlist the fleet</div>
                    <h3 className="mt-2 font-serif text-3xl tracking-display text-[#FAFAFA]">
                        Three ranks. One Wayfinder.
                    </h3>
                </div>
                <Link
                    to={{ pathname: "/foundry/interview", search }}
                    data-testid="vision-cta-interview"
                    className="inline-flex items-center gap-2 rounded-sm px-6 py-3 text-sm font-medium"
                    style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                >
                    Begin the Director Interview <ArrowRight size={14} />
                </Link>
            </section>
        </div>
    );
}

function HorizonBadge({ state }) {
    const map = {
        live: { label: "LIVE", style: { background: "rgba(52, 211, 153, 0.1)", color: "#34D399" } },
        build: { label: "BUILDING", style: { background: "rgba(201,169,110,0.1)", color: "var(--brand-accent)" } },
        design: { label: "IN DESIGN", style: { background: "rgba(165,180,252,0.1)", color: "#A5B4FC" } },
        mission: { label: "MISSION", style: { background: "rgba(248,113,113,0.1)", color: "#F87171" } },
    };
    const cfg = map[state] || map.design;
    return (
        <span className="rounded-full px-2.5 py-0.5 text-[10px] tracking-widest" style={cfg.style}>
            {cfg.label}
        </span>
    );
}
